import { useEffect, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import ThreeDotsLoader from '@/Components/Loader/ThreeDotsLoader/ThreeDotsLoader';
import { Button } from '@/Components/UI/Button/Button';
import TextInput from '@/Components/UI/Form/TextInput/TextInput';
import { hasNoWritePermission } from '@/helpers/spreadsheetHelper';
import useOnClickOutside from '@/hooks/useClickOutside';
import { datasetSliceName, spreadsheetSliceName, userDataSliceName } from '@/redux/constants';
import {
  renameColumn,
  setIsRenamePopoverOpen,
  setRenamePopoverCoordinates,
} from '@/redux/reducers/spreadsheetSlice';
import { get, put } from '@/Utils/API';
import { FILES, HEADERS } from '@/Utils/constants';
import { getFileUuidFromPath } from '@/Utils/getFileUuidFromPath';

export const RenameColumnPopover = () => {
  const { user, isViewerAnalyst } = useSelector((state) => state[userDataSliceName]);
  const { currentFile } = useSelector((state) => state[datasetSliceName]);
  const { renameColumnData } = useSelector((state) => state[spreadsheetSliceName]);
  const { colId, columnName, hide } = renameColumnData || {};
  const inputRef = useRef();
  const listRef = useRef();
  const dispatch = useDispatch();
  const [newColumnName, setNewColumnName] = useState(columnName);

  const revenueBaseAccess =
    !!user?.userProperties?.revenueBaseAccess && hasNoWritePermission(currentFile);

  useOnClickOutside(listRef, () => dispatch(setIsRenamePopoverOpen(false)));

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.selectionStart = inputRef.current.value.length;
      inputRef.current.selectionEnd = inputRef.current.value.length;
    }
  }, []);

  const setPopoverCoordinates = (headerCellElement) => {
    return new Promise((resolve) => {
      const rect = headerCellElement.getBoundingClientRect();

      dispatch(setRenamePopoverCoordinates({ top: rect.bottom - 80, left: rect.left - 46 }));

      resolve(headerCellElement);
    });
  };
  const getHeaderCellElement = (colId) => {
    return new Promise((resolve, reject) => {
      const headerCellSelector = `.ag-header-cell[col-id="${colId}"]`;
      const headerCellElement = document.querySelector(headerCellSelector);
      if (headerCellElement) {
        resolve(headerCellElement);
      } else {
        reject(new Error('Header cell element not found'));
      }
    });
  };
  const delay = (ms) => {
    return new Promise((resolve) => setTimeout(resolve, ms));
  };

  useEffect(() => {
    if (hide) {
      dispatch(
        setRenamePopoverCoordinates({
          top: '42%',
          left: '36%',
          transform: 'translate(-42%, -30%)',
        })
      );

      return;
    }
    dispatch(setRenamePopoverCoordinates(null));
    delay(0)
      .then(() => getHeaderCellElement(colId))
      .then((headerCellElement) => {
        setPopoverCoordinates(headerCellElement);
      });
    return () => dispatch(setRenamePopoverCoordinates(null));
  }, []);

  const changeColumnName = async () => {
    const uuid = getFileUuidFromPath();
    const args = {
      headers: {
        [colId]: newColumnName,
      },
    };

    try {
      await put(`${FILES}/${uuid}/${HEADERS}`, args);
      dispatch(renameColumn({ colId, newColumnName }));
    } catch (err) {
      throw new Error(err);
    } finally {
      dispatch(setIsRenamePopoverOpen(false));
    }
  };

  const loader = (top) => {
    return (
      <div
        className={`absolute top-[${top}px] left-0 z-10 flex justify-center items-center`}
        data-cy='loader'
      >
        <ThreeDotsLoader text='' className='flex items-center justify-center' />
      </div>
    );
  };

  return (
    <div
      data-cy='rename-column-popover'
      ref={listRef}
      className='p-4 pb-2 bg-white w-[320px] shadow-renameColumn rounded-xl'
    >
      <div>
        <TextInput
          readOnly={isViewerAnalyst || revenueBaseAccess}
          maxLength={200}
          dataCy='rename-column-popover-input'
          name='column-name'
          ref={inputRef}
          placeholder='Enter new column name'
          value={newColumnName}
          className='w-full'
          onChange={(e) => setNewColumnName(e.target.value)}
        />
      </div>
      <hr className='mt-4 mb-2 solid'></hr>
      <div className='flex justify-end gap-4'>
        <Button
          onClick={() => dispatch(setIsRenamePopoverOpen(false))}
          variant='ghost'
          color='shadow'
          size='medium'
          dataCy='cancel-btn'
        >
          Cancel
        </Button>
        <Button
          color='oceanBlue'
          size='medium'
          onClick={changeColumnName}
          disabled={!newColumnName || isViewerAnalyst || revenueBaseAccess}
          dataCy='save-btn'
        >
          Save
        </Button>
      </div>
    </div>
  );
};
